import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminNotificationPolicyList,
  updateYowanAdminNotificationPolicyDecision,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const policySortOptions = [
  { value: 'event-asc', label: 'イベント昇順' },
  { value: 'event-desc', label: 'イベント降順' },
  { value: 'category-asc', label: '種別昇順' },
  { value: 'category-desc', label: '種別降順' },
  { value: 'decision-asc', label: '判断昇順' },
  { value: 'audience-asc', label: '宛先昇順' },
];

const decisionOptions = [
  { value: 'undecided', label: '未決定' },
  { value: 'send', label: '送信する' },
  { value: 'skip', label: '送信しない' },
];

const indexColumnStyle = {
  width: '2.25rem',
  minWidth: '2.25rem',
  maxWidth: '2.25rem',
  paddingLeft: '0.5rem',
  paddingRight: '0.5rem',
  whiteSpace: 'nowrap',
};

const eventColumnStyle = {
  width: '17rem',
  minWidth: '17rem',
};

const eventCellStyle = {
  ...eventColumnStyle,
  lineHeight: 1.28,
};

const eventSublineStyle = {
  lineHeight: 1.22,
  marginTop: '0.1rem',
};

const decisionColumnStyle = {
  width: '11rem',
  minWidth: '11rem',
  whiteSpace: 'nowrap',
};

const audienceColumnStyle = {
  width: '14rem',
  minWidth: '14rem',
};

const actionColumnStyle = {
  width: '11rem',
  minWidth: '11rem',
  whiteSpace: 'nowrap',
};

const tableStyle = {
  minWidth: '64rem',
};

const sortPolicyRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'event-desc') {
      return right.eventName.localeCompare(left.eventName, 'ja');
    }

    if (sortKey === 'decision-asc') {
      return left.decisionStatusLabel.localeCompare(right.decisionStatusLabel, 'ja');
    }

    if (sortKey === 'category-desc') {
      return right.category.localeCompare(left.category, 'en');
    }

    if (sortKey === 'category-asc') {
      return left.category.localeCompare(right.category, 'en');
    }

    if (sortKey === 'audience-asc') {
      return left.audienceSummary.localeCompare(right.audienceSummary, 'ja');
    }

    return left.eventName.localeCompare(right.eventName, 'ja');
  });

  return nextRows;
};

const AdminNotificationPoliciesListPage = () => {
  const content = yowanPageContent['/admin/notifications/policies'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('event-asc');
  const [updatingEventKey, setUpdatingEventKey] = useState('');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminNotificationPolicyList()
      .then((payload) => {
        if (isMounted) {
          setRows(payload.results);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRows([]);
          setLoadError('通知ルールを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!content) {
    return null;
  }

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRows = normalizedQuery
    ? rows.filter((row) =>
        [
          row.category,
          row.eventName,
          row.eventKey,
          row.decisionStatusLabel,
          row.defaultActionLabel,
          row.audienceSummary,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedQuery)),
      )
    : rows;
  const visibleRows = sortPolicyRows(filteredRows, sortKey);

  const replacePolicyRow = (nextPolicy) => {
    setRows((currentRows) =>
      currentRows.map((row) => (row.eventKey === nextPolicy.eventKey ? nextPolicy : row)),
    );
  };

  const handleDecisionChange = async (row, nextDecisionStatus) => {
    if (row.decisionStatus === nextDecisionStatus) {
      return;
    }

    setUpdatingEventKey(row.eventKey);

    try {
      const nextPolicy = await updateYowanAdminNotificationPolicyDecision(row.eventKey, nextDecisionStatus);
      replacePolicyRow(nextPolicy);
      alert('送信判断を更新しました。');
    } catch (error) {
      alert('送信判断を更新できませんでした。');
    } finally {
      setUpdatingEventKey('');
    }
  };

  const handleResetToDefault = async (row) => {
    if (!window.confirm('この通知ルールを既定値に戻しますか？')) {
      return;
    }

    setUpdatingEventKey(row.eventKey);

    try {
      const nextPolicy = await updateYowanAdminNotificationPolicyDecision(
        row.eventKey,
        row.defaultDecisionStatus,
      );
      replacePolicyRow(nextPolicy);
      alert('既定値に戻しました。');
    } catch (error) {
      alert('既定値に戻せませんでした。');
    } finally {
      setUpdatingEventKey('');
    }
  };

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="dispatches" to={appendYowanPreviewQueryIfNeeded('/admin/notifications', preservedDomainSearch)}>
            通知履歴へ
          </Link>,
          <Link key="recipients" to={appendYowanPreviewQueryIfNeeded('/admin/notifications/recipients', preservedDomainSearch)}>
            通知宛先へ
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => setSearchQuery(event.target.value)}
        sortValue={sortKey}
        onSortChange={(event) => setSortKey(event.target.value)}
        metaText={`${visibleRows.length}件を表示`}
        searchPlaceholder="イベント / キー / 種別 / 判断 / 宛先"
        sortOptions={policySortOptions}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table" style={tableStyle}>
          <thead>
            <tr>
              <th scope="col" style={indexColumnStyle}>#</th>
              <th scope="col" style={eventColumnStyle}>イベント / 種別 / キー</th>
              <th scope="col" style={decisionColumnStyle}>送信判断</th>
              <th scope="col" style={audienceColumnStyle}>宛先</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => (
              <tr key={row.eventKey || index}>
                <td style={indexColumnStyle}>{index + 1}</td>
                <td style={eventCellStyle}>
                  <p className="yowan-admin-resource-table__title">{row.eventName}</p>
                  <p className="yowan-admin-resource-table__subline" style={eventSublineStyle}>
                    {row.category} / {row.eventKey}
                  </p>
                </td>
                <td style={decisionColumnStyle}>
                  <select
                    className="c-form__control"
                    value={row.decisionStatus}
                    disabled={updatingEventKey === row.eventKey}
                    aria-label={`${row.eventName}の送信判断`}
                    onChange={(event) => handleDecisionChange(row, event.target.value)}
                  >
                    {decisionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={audienceColumnStyle}>{row.audienceSummary || '-'}</td>
                <td style={actionColumnStyle}>
                  <div className="yowan-admin-resource-table__actions">
                    <Link to={appendYowanPreviewQueryIfNeeded(row.detailHref, preservedDomainSearch)}>詳細</Link>
                    <button
                      type="button"
                      disabled={updatingEventKey === row.eventKey || row.decisionStatus === row.defaultDecisionStatus}
                      onClick={() => handleResetToDefault(row)}
                    >
                      既定値に戻す
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!visibleRows.length ? (
              <tr>
                <td colSpan="5">
                  {isLoading ? <LoadingSpinner size="sm" /> : loadError || '通知ルールはまだありません。'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </AdminBlogShell>
  );
};

export default AdminNotificationPoliciesListPage;
